import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout, DocsTable } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasPrivacy() {
  return (
    <>
      <NextSeo
        title="Atlas roles, privacy and key delivery | Chat.co"
        description="Understand Atlas permissions and handle private key delivery safely."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Atlas roles, privacy and key delivery"
        description="Use individual accounts and keep credentials out of conversations."
        readingTime="4 min read"
        tableOfContents={[
          { id: 'roles', title: 'Roles and bot permissions' },
          { id: 'privacy', title: 'Share only what is needed' },
          { id: 'keys', title: 'Private key delivery' },
        ]}
        prevArticle={{
          title: 'Atlas knowledge and answer quality',
          href: PageLink.docsAtlasKnowledge,
        }}
        nextArticle={{ title: 'Troubleshooting Atlas', href: PageLink.docsAtlasTroubleshooting }}>
        <section id="roles">
          <h2>Roles and bot permissions</h2>
          <p>
            Atlas is available to enrolled accounts with the required permissions. Account access
            does not enable every capability or grant access to another account&apos;s bots.
          </p>
          <DocsTable
            headers={['Role', 'Atlas access']}
            rows={[
              ['Owner', 'Account access, subject to enrollment and capability availability.'],
              [
                'Admin',
                'Use Atlas must be allowed; account-level actions need their individual admin permissions. Owner-only actions remain with the owner.',
              ],
              [
                'Team member',
                'Atlas assistant access on assigned bots; bot feature and action permissions still apply.',
              ],
            ]}
          />
          <p>
            Team members cannot approve high-risk changes such as publishing, domain locks, creating
            keys, checkouts or connector removal. They cannot perform account-level actions such as
            billing, inviting staff or creating bots, owner-only actions, or deletes through Atlas.
          </p>
          <p>
            An admin with billing permission can prepare checkout when that capability is available.
            Preparing checkout does not complete a payment. Owner-only actions remain restricted to
            the account owner, including for admins. Do not share an owner login to work around a
            denied action. Use the <Link href={PageLink.staff}>Team page</Link> and{' '}
            <Link href={PageLink.docsTeamRoles}>team roles guide</Link> to review assigned access.
          </p>
          <p>
            Each consequential action needs its own review card and an explicit click on its
            approval button. Use Apply on hosted workflow reviews or Approve and its send, publish
            or delete variants on standard reviews. An ordinary chat yes is not approval, and a
            permission grant is not approval of a particular effect.
          </p>
        </section>
        <section id="privacy">
          <h2>Share only what is needed</h2>
          <p>
            Use the minimum information needed for the task. Do not paste API keys, passwords or
            login tokens into Atlas, source documents or support messages. Review drafts and
            recipients before approving anything sent outside your account.
          </p>
          <p>
            For retention and data-handling details, read the{' '}
            <Link href={PageLink.docsSecurityCompliance}>security and compliance guide</Link> and{' '}
            <Link href={PageLink.privacy}>privacy policy</Link>. Do not infer a privacy or security
            guarantee from a task status.
          </p>
        </section>
        <section id="keys">
          <h2>Private key delivery</h2>
          <DocsCallout title="Only when key creation is available">
            When an approved publishable-key creation offers private delivery, select Show key
            privately. Copy it into the intended configuration while it is visible. The display
            hides it after one minute; Hide key hides it sooner. It cannot be retrieved again from
            that delivery.
          </DocsCallout>
          <p>
            The key is delivered separately from the conversation. Keep the actual value out of
            screenshots, chat, documentation and support reports. Check the intended use and domain
            restrictions before installing it.
          </p>
          <p>
            If delivery expires, fails or the response is lost, do not repeatedly request it or
            assume no key was created. Check the bot&apos;s Settings and existing keys first. Check
            the outcome before creating any replacement. If the delivery has expired, the app points
            to Settings to create a new key. Delivery expiry does not establish that no key exists.
            If the delivery was already issued, contact support to rotate the key. Any new creation
            in Atlas still needs its own review and explicit approval, where available. If you
            cannot establish the result,{' '}
            <Link href={PageLink.contactUs}>contact support without including the key</Link>.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
